import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { TrendingUp, Activity, TrendingDown, ArrowRight, Info } from 'lucide-react';
import StockService from '../../Api_Module/Api_Services/StockService';
import { formatNumber } from '../../utils/Numberformat';
import { useTheme } from '../ThemeContext';

const FundInsightsPreview: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [totals, setTotals] = useState({
        calls: 0,
        puts: 0,
        newPositions: 0
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch top 5 for a quick snapshot
                const topN = 5;
                const [callsRes, putsRes, newPosRes] = await Promise.all([
                    StockService.getTopByCallOptions(topN),
                    StockService.getTopByPutOptions(topN),
                    StockService.getTopByNewPositions(topN)
                ]);

                const getArr = (res: any) => {
                    if (!res) return [];
                    if (Array.isArray(res)) return res;
                    if (Array.isArray(res.data)) return res.data;
                    if (Array.isArray(res.items)) return res.items;
                    return [];
                };

                const calls = getArr(callsRes);
                const puts = getArr(putsRes);
                const newPos = getArr(newPosRes);

                setTotals({
                    calls: calls.slice(0, topN).reduce((acc: number, item: any) => acc + (item.totalCalls || item.value || 0), 0),
                    puts: puts.slice(0, topN).reduce((acc: number, item: any) => acc + (item.totalPuts || item.value || 0), 0),
                    newPositions: newPos.slice(0, topN).reduce((acc: number, item: any) => acc + (item.newPositions || item.count || 0), 0)
                });
            } catch (error) {
                console.error('Error fetching fund insights preview:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const textPrimary = theme === 'dark' ? 'text-theme-primary' : 'text-gray-900';
    const textSecondary = theme === 'dark' ? 'text-theme-muted' : 'text-gray-600';

    return (
        <div className="w-full animate-fadeIn">
            <div className="flex items-center justify-between mb-4">
                <h2 className={`text-xl font-bold ${textPrimary}`}>Institutional Market Pulse</h2>
                <button
                    onClick={() => navigate('/fund-insights')}
                    className={`group flex items-center gap-1 text-sm font-medium transition-colors ${theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                >
                    View Full Insights
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Bullish Sentiment */}
                <div
                    onClick={() => navigate('/fund-insights')}
                    className={`p-5 rounded-xl border cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02] ${theme === 'dark' ? 'bg-gradient-to-br from-green-900/20 to-emerald-900/10 border-green-500/20' : 'bg-gradient-to-br from-green-50 to-emerald-50/50 border-green-200'}`}
                >
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-green-500/10 text-green-600 dark:text-green-400">
                                <TrendingUp className="w-5 h-5" />
                            </div>
                            <span className="font-semibold text-green-700 dark:text-green-400">Bullish</span>
                        </div>
                        <Info className={`w-4 h-4 ${textSecondary} opacity-50`} />
                    </div>
                    <div className={`text-2xl font-bold mb-1 ${textPrimary}`}>
                        {loading ? '...' : formatNumber(totals.calls)}
                    </div>
                    <div className={`text-xs ${textSecondary}`}>Total Call Options (Top 5)</div>
                </div>

                {/* High Conviction */}
                <div
                    onClick={() => navigate('/fund-insights')}
                    className={`p-5 rounded-xl border cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02] ${theme === 'dark' ? 'bg-gradient-to-br from-blue-900/20 to-indigo-900/10 border-blue-500/20' : 'bg-gradient-to-br from-blue-50 to-indigo-50/50 border-blue-200'}`}
                >
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                                <Activity className="w-5 h-5" />
                            </div>
                            <span className="font-semibold text-blue-700 dark:text-blue-400">Conviction</span>
                        </div>
                        <Info className={`w-4 h-4 ${textSecondary} opacity-50`} />
                    </div>
                    <div className={`text-2xl font-bold mb-1 ${textPrimary}`}>
                        {loading ? '...' : totals.newPositions.toLocaleString()}
                    </div>
                    <div className={`text-xs ${textSecondary}`}>New Positions Opened (Top 5)</div>
                </div>

                {/* Bearish Sentiment */}
                <div
                    onClick={() => navigate('/fund-insights')}
                    className={`p-5 rounded-xl border cursor-pointer transition-all duration-300 hover:shadow-md hover:scale-[1.02] ${theme === 'dark' ? 'bg-gradient-to-br from-red-900/20 to-orange-900/10 border-red-500/20' : 'bg-gradient-to-br from-red-50 to-orange-50/50 border-red-200'}`}
                >
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-red-500/10 text-red-600 dark:text-red-400">
                                <TrendingDown className="w-5 h-5" />
                            </div>
                            <span className="font-semibold text-red-700 dark:text-red-400">Bearish</span>
                        </div>
                        <Info className={`w-4 h-4 ${textSecondary} opacity-50`} />
                    </div>
                    <div className={`text-2xl font-bold mb-1 ${textPrimary}`}>
                        {loading ? '...' : formatNumber(totals.puts)}
                    </div>
                    <div className={`text-xs ${textSecondary}`}>Total Put Options (Top 5)</div>
                </div>
            </div>
        </div>
    );
};

export default FundInsightsPreview;
